import { Box, Typography } from '@mui/material';
import React from 'react';
import {
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormSetValue,
    UseFormTrigger,
} from 'react-hook-form';
import { environmentSafeLog } from '../../../utils/environment';
import CompositeAssetSelection from '../CompositeAssetSelection';

// Form data interface (matching RegisterAssetPage)
interface FormData {
  layer: string;
  layerName: string;
  categoryCode: string;
  categoryName: string;
  subcategoryCode: string;
  subcategoryName: string;
  subcategoryNumericCode: string;
  name: string;
  description: string;
  source: string;
  tags: string[];
  files: File[];
  hfn: string;
  mfa: string;
  sequential: string;
  trainingData?: any;
  layerSpecificData?: {
    components: any[];
  };
  albumArtUrl?: string;
  albumArtSource?: string;
  albumArtQuality?: string;
  extractedSongData?: any;
  bpm?: number;
  songMetadata?: any;
  aiMetadata?: any;
}

interface CompositeSelectionStepProps {
  // Form methods from React Hook Form
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
  trigger: UseFormTrigger<FormData>;
  handleSubmit: UseFormHandleSubmit<FormData>;

  // Navigation
  onBack: () => void;
  onSubmit: (data: FormData) => void;

  // Composite AI processing callback
  onTriggerCompositeAI: (components: any[]) => void;

  // Validation state
  compositeValidationErrors: string[];
  onValidationChange: (errors: string[]) => void;

  // Status
  isSubmitting?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: string;

  // Layer info
  watchLayer: string;
  isCompositeLayer: boolean;

  // Resolved taxonomy for HFN preview (from context; avoids formData/getValues returning objects)
  resolvedLayer?: string;
  resolvedCategoryCode?: string;
  resolvedSubcategoryCode?: string;
}

/**
 * Step 5: Composite Asset Selection
 *
 * This component handles the composite asset workflow:
 * - Component search and selection
 * - Automatic AI processing when components are selected
 * - Form validation for composite requirements
 * - Direct submission for composite assets
 *
 * Only shown for composite (C) layer assets
 */
const CompositeSelectionStep: React.FC<CompositeSelectionStepProps> = ({
  setValue,
  getValues,
  trigger,
  handleSubmit,
  onBack,
  onSubmit,
  onTriggerCompositeAI,
  compositeValidationErrors,
  onValidationChange,
  isSubmitting = false,
  loading = false,
  disabled = false,
  error,
  watchLayer,
  isCompositeLayer,
  resolvedLayer,
  resolvedCategoryCode,
  resolvedSubcategoryCode,
}) => {
  // Error display
  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  // Only render for composite layers
  if (!isCompositeLayer) {
    return <div>Invalid step for this asset type</div>;
  }

  const content = (
    <Box>
      <CompositeAssetSelection
        targetLayer={resolvedLayer ?? getValues('layer')}
        layerName={getValues('layerName')}
        categoryCode={resolvedCategoryCode ?? getValues('categoryCode')}
        subcategoryCode={resolvedSubcategoryCode ?? getValues('subcategoryCode')}
        onComponentsSelected={components => {
          environmentSafeLog(
            `[REGISTER PAGE] Components selected callback triggered`
          );
          environmentSafeLog(
            `[REGISTER PAGE] Components received:`,
            components
          );
          environmentSafeLog(
            `[REGISTER PAGE] Component count: ${components.length}`
          );

          // Set the form value
          setValue('layerSpecificData.components', components);

          environmentSafeLog(
            `[COMPOSITE AI] Components form data set for AI processing:`,
            components.length
          );

          // Verify the setValue worked
          const currentValue = getValues('layerSpecificData.components');
          environmentSafeLog(
            `[REGISTER PAGE] Verification - form value after setValue:`,
            currentValue
          );
          environmentSafeLog(
            `[REGISTER PAGE] setValue successful:`,
            JSON.stringify(currentValue) === JSON.stringify(components)
          );

          // Force trigger validation to update form state
          trigger('layerSpecificData.components');

          // Additional verification after trigger
          setTimeout(() => {
            const reVerify = getValues('layerSpecificData.components');
            environmentSafeLog(
              `[REGISTER PAGE] Re-verification after trigger:`,
              reVerify
            );
          }, 100);

          // 🎯 STEP 5 AUTOMATIC AI PROCESSING: Trigger AI generation when components selected
          if (components.length > 0) {
            environmentSafeLog(
              `[COMPOSITE AI] Triggering automatic AI processing for ${components.length} components`
            );

            // Add a small delay to ensure UI is updated
            setTimeout(() => {
              onTriggerCompositeAI(components);
            }, 500);
          }
        }}
        onValidationChange={errors => {
          onValidationChange(errors);
        }}
        initialComponents={getValues('layerSpecificData.components') || []}
      />
    </Box>
  );

  // If disabled, show overlay
  if (disabled) {
    return (
      <Box sx={{ position: 'relative', opacity: 0.6, pointerEvents: 'none' }}>
        {content}
      </Box>
    );
  }

  // If loading, show loading overlay
  if (loading) {
    return (
      <Box sx={{ position: 'relative' }}>
        {content}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Typography>Loading composite assets...</Typography>
        </Box>
      </Box>
    );
  }

  return content;
};

export default CompositeSelectionStep;
